import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Banner, Button, InlineStack } from "@shopify/polaris";

const REVIEW_EVENT = "analyticshero:review-value";
const REVIEW_URL =
  "https://apps.shopify.com/analyticshero#modal-show=WriteReviewModal";

async function reviewRequest(shop, data) {
  if (!window.shopify?.idToken) {
    throw new Error("Please open AnalyticsHero from Shopify Admin.");
  }

  const token = await window.shopify.idToken();

  return axios.post("/analytics/review-request/", data, {
    params: { shop },
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function notifyReviewValue(shop, event, extra = {}) {
  window.dispatchEvent(
    new CustomEvent(REVIEW_EVENT, {
      detail: { ...extra, shop, event },
    })
  );
}

// Explicit, merchant-initiated review action.
// This does not create an automatic invitation or claim a submitted review.
function openReviewPage() {
  window.open(REVIEW_URL, "_blank", "noopener,noreferrer");
  return { success: true, code: "review_page_opened" };
}

export function useReviewTrigger() {
  return useCallback(() => openReviewPage(), []);
}

export default function ReviewManager({
  shopOrigin,
  onTriggerAvailable,
}) {
  const [invitation, setInvitation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const visible = useRef(false);
  const requesting = useRef(false);

  const manualTrigger = useCallback(() => openReviewPage(), []);

  useEffect(() => {
    onTriggerAvailable?.(manualTrigger);
  }, [manualTrigger, onTriggerAvailable]);

  useEffect(() => {
    if (!shopOrigin) return;

    let cancelled = false;
    let timer;

    visible.current = false;
    requesting.current = false;
    setInvitation(null);
    setError("");

    async function requestInvitation(detail) {
      if (
        cancelled ||
        visible.current ||
        requesting.current ||
        document.visibilityState !== "visible"
      ) {
        return;
      }

      requesting.current = true;

      try {
        const response = await reviewRequest(shopOrigin, {
          action: "interaction",
          event: detail.event,
          check: detail.check,
        });

        if (!cancelled && response.data.show) {
          visible.current = true;
          setInvitation(response.data);
        }
      } catch (err) {
        // Fail closed: no invitation when eligibility cannot be checked.
        console.error("[ReviewManager] Eligibility unavailable:", err);
      } finally {
        if (!cancelled) requesting.current = false;
      }
    }

    function handleValue(event) {
      const detail = event.detail;

      if (!detail || detail.shop !== shopOrigin) return;
      if (visible.current || requesting.current) return;

      clearTimeout(timer);

      // Allow the merchant to view the recommendation/results first.
      timer = setTimeout(() => requestInvitation(detail), 5000);
    }

    window.addEventListener(REVIEW_EVENT, handleValue);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener(REVIEW_EVENT, handleValue);
    };
  }, [shopOrigin]);

  async function answer(action) {
    if (!invitation || busy) return;

    setBusy(true);
    setError("");

    try {
      await reviewRequest(shopOrigin, {
        action,
        prompt_id: invitation.prompt_id,
      });

      visible.current = false;
      setInvitation(null);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "We couldn’t save your choice. Please try again."
      );
    } finally {
      setBusy(false);
    }
  }

  if (!invitation) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <Banner title="Share your experience with AnalyticsHero" tone="info">
        <p>
          Your feedback helps us improve AnalyticsHero for merchants like you.
          Would you like to leave a review?
        </p>

        <div style={{ marginTop: 12 }}>
          <InlineStack gap="300" blockAlign="center">
            <Button
              url={REVIEW_URL}
              external
              disabled={busy}
              onClick={() => {
                // Navigation remains a normal link, avoiding popup blockers.
                void answer("review_clicked");
              }}
            >
              Leave a review
            </Button>

            <Button disabled={busy} onClick={() => answer("later")}>
              Later
            </Button>

            <Button
              variant="plain"
              disabled={busy}
              onClick={() => answer("decline")}
            >
              Don’t ask again
            </Button>
          </InlineStack>
        </div>

        {error && (
          <p role="alert" style={{ marginTop: 12 }}>
            {error}
          </p>
        )}
      </Banner>
    </div>
  );
}